import { Link } from "@mui/material";
import ExpandableTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/ExpandableTable";
import { format } from "date-fns";
import { withErrorBoundary } from "hocs/withErrorBoundary";
import useGlobalContext from "hooks/globalData";
import { useStrictQuery } from "hooks/useStrictQuery";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Link as RouterLink } from "react-router-dom";
import {
  AuditLogEntry,
  AuditLogUser,
  ListRequestOfAuditLogRequest,
} from "services/KnowfullyAdminClient";
import { standardDateTimeWithSecondsFormat } from "utils/dateUtils";

type UserAuditLogTableProps = {
  userId: string;
};

const styles = {
  expandedRows: {
    padding: "8px 16px",
  },
  expandedRow: {
    padding: "2px 0",
  },
  partialWarning: {
    marginBottom: "8px",
  },
};

const UserAuditLogTable = ({ userId }: UserAuditLogTableProps) => {
  const { formatMessage: t } = useIntl();
  const { loggingService, userDetailsUrl } = useGlobalContext();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [order, setOrder] = useState<TableSortType>("desc");
  const [orderBy, setOrderBy] = useState("logDate");

  const request = ListRequestOfAuditLogRequest.fromJS({
    pageNumber: page,
    pageSize: rowsPerPage,
    sortField: orderBy,
    sortDescending: order === "desc",
    parameters: {
      entityId: userId,
    },
  });

  const result = useStrictQuery(
    ["loggingService.getUserAuditLog", request],
    () => loggingService.getUserAuditLog(request),
    {
      enabled: !!userId,
    },
  );

  const ActorView = ({ entry }: { entry: AuditLogEntry }) => {
    const actor: AuditLogUser | undefined = entry.adminUser ?? entry.user;
    const displayName = [actor?.firstName, actor?.lastName]
      .filter(Boolean)
      .join(" ");

    if (!actor?.organizationUserId) {
      return <span>{displayName || t({ id: "unknownActor" })}</span>;
    }

    return (
      <Link
        component={RouterLink}
        to={userDetailsUrl(actor.organizationUserId)}
      >
        {displayName || t({ id: "unknownActor" })}
      </Link>
    );
  };

  const cells: TableCellType[] = [
    {
      label: t({ id: "activityDate" }),
      accessor: "logDate",
      type: "node",
      View: (row: { value: AuditLogEntry }) => {
        if (!row.value.logDate) return null;
        return (
          <span>
            {format(row.value.logDate, standardDateTimeWithSecondsFormat)}
          </span>
        );
      },
    },
    {
      label: t({ id: "auditEvent" }),
      accessor: "eventTypeName",
      type: "text",
    },
    {
      label: t({ id: "actor" }),
      accessor: "adminUser",
      type: "node",
      noSort: true,
      View: (row: { value: AuditLogEntry }) => <ActorView entry={row.value} />,
    },
  ];

  // Memoised because the shared table re-mounts an expanded row whenever its props change identity, which
  // would collapse the panel under the reader on every refetch.
  const rows = useMemo(
    () =>
      (result.data?.data ?? []).map((entry) => ({
        ...entry,
        id: entry.id!,
        isExpandable: !!entry.auditLogEntryRows?.length,
        renderExpandedData: () => (
          <div style={styles.expandedRows}>
            {(entry.auditLogEntryRows ?? []).map((row, index) => (
              <div key={index} style={styles.expandedRow}>
                <strong>{row.name}</strong>
                {": "}
                {row.priorValue == null ? (
                  row.value
                ) : (
                  <>
                    {row.priorValue}
                    {" → "}
                    {row.value ?? t({ id: "notSet" })}
                  </>
                )}
              </div>
            ))}
          </div>
        ),
      })),
    [result.data?.data, t],
  );

  return (
    <>
      <ExpandableTable
        rows={rows}
        cells={cells}
        rowCount={result.data?.totalRecords ?? 0}
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        order={order}
        setOrder={setOrder}
        orderBy={orderBy}
        setOrderBy={setOrderBy}
        loading={result.isLoading}
        collapsable
        dense
        hideEmptyRows
      />
    </>
  );
};

export default withErrorBoundary(UserAuditLogTable);
